import { useCallback, useEffect, useMemo } from 'react';
import { BulkActionItem } from 'src/components/Listings/InventoryActionDropdown/components/types';
import { SALES_BULK_DEALLOCATE_CANCEL_SALES_UPDATE_KEY } from 'src/components/Sales/SalesActionDropdown';
import {
  IPosEntity,
  useActivePosEntityContext,
} from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { BulkDeallocateCancelSalesDialog } from 'src/dialogs/BulkEdits/BulkDeallocateCancelSalesDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  BulkEditPreviewWithDetails,
  BulkEditSaleClient,
  CommandOptionType,
  Event,
  SaleCancellationReason,
  SaleQuery,
} from 'src/WebApiController';

import { LaunchBulkDeallocateCancelSales } from './LaunchBulkDeallocateCancelSales';

export const useBulkDeallocateCancelSalesAction = (
  filterQueryWithEventIds: SaleQuery,
  deallocateOnly: boolean,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  event?: Event
) => {
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const disabled = false;

  const { activeAccountWebClientConfig } = useAppContext();
  const { setActivePosEntity } = useActivePosEntityContext<IPosEntity>();
  const updateKey = event
    ? `${SALES_BULK_DEALLOCATE_CANCEL_SALES_UPDATE_KEY}-event-${event.virtId}`
    : SALES_BULK_DEALLOCATE_CANCEL_SALES_UPDATE_KEY;
  const {
    refreshCatalog,
    eventsExpansion: { setListItemExpansion, refreshExpandedListItems },
  } = useCatalogDataContext();
  const { showErrorDialog } = useClientLogContext();

  const {
    openDialog: openBulkMarkAsFulfilledDialog,
    closeDialog: closeBulkMarkAsFulfilledDialog,
  } = useDialog(
    DialogId.BulkDeallocateCancelSales,
    BulkDeallocateCancelSalesDialog
  );

  const onSubmit = useCallback(
    async (
      removeArtifacts: boolean | null,
      adminHold: boolean | null,
      keepAllottedTickets: boolean | null,
      cancellationReason: SaleCancellationReason | null,
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const preview = await new BulkEditSaleClient(
              activeAccountWebClientConfig
            ).bulkDeallocateCancelSalePreview(filterQueryWithEventIds);

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkDeallocateCancelSalePreview', error, {
              trackErrorData: { event },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else {
        await tryInvokeApi(
          async () => {
            const saleClient = new BulkEditSaleClient(
              activeAccountWebClientConfig
            );

            const succeeded = await saleClient.bulkDeallocateCancelSale(
              { item1: preview!.preview, item2: cancellationReason },
              deallocateOnly,
              removeArtifacts,
              adminHold,
              keepAllottedTickets,
              updateKey,
              supportBackgroundProcess
            );

            if (!supportBackgroundProcess) {
              if (succeeded) {
                // Invalidate the activePosEntity, so that when clicking on listingDetails
                // we force re-loading the listing and newest pricing settings can be displayed
                setActivePosEntity(0);
                await refreshExpandedListItems();
              }

              closeBulkMarkAsFulfilledDialog();
              await refreshCatalog();
            }
          },
          (error) => {
            showErrorDialog('bulkDeallocateCancelSale', error, {
              trackErrorData: { preview },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      setIsLoading,
      activeAccountWebClientConfig,
      filterQueryWithEventIds,
      showErrorDialog,
      event,
      deallocateOnly,
      updateKey,
      closeBulkMarkAsFulfilledDialog,
      refreshCatalog,
      setActivePosEntity,
      refreshExpandedListItems,
    ]
  );

  const onChangeClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (event) {
        setListItemExpansion(true, [event.virtId]);
      }

      openBulkMarkAsFulfilledDialog({
        deallocateOnly,
        updateKey,
        onOkay: onSubmit,
        onClosed: () => {
          setIsLoading(false);
        },
        onCancel: closeBulkMarkAsFulfilledDialog,
      });
    },
    [
      closeBulkMarkAsFulfilledDialog,
      deallocateOnly,
      event,
      onSubmit,
      openBulkMarkAsFulfilledDialog,
      setIsLoading,
      setListItemExpansion,
      updateKey,
    ]
  );

  const openDeallocateDialogStableRef = useEventCallback(() => {
    if (event) {
      setListItemExpansion(true, [event.virtId]);
    }

    openBulkMarkAsFulfilledDialog({
      deallocateOnly: true,
      updateKey,
      onOkay: onSubmit,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkMarkAsFulfilledDialog,
    });
  });

  const openRejectDialogStableRef = useEventCallback(() => {
    if (event) {
      setListItemExpansion(true, [event.virtId]);
    }

    openBulkMarkAsFulfilledDialog({
      deallocateOnly: false,
      updateKey,
      onOkay: onSubmit,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkMarkAsFulfilledDialog,
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.SalesBulkEditDeallocate,
        onSelect: openDeallocateDialogStableRef,
      },
      disabled,
    });

    registerCommand({
      command: {
        id: CommandOptionType.SalesBulkEditReject,
        onSelect: openRejectDialogStableRef,
      },
      disabled,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.SalesBulkEditDeallocate,
        disabled,
      });
      unregisterCommand({
        commandId: CommandOptionType.SalesBulkEditReject,
        disabled,
      });
    };
  }, [
    registerCommand,
    unregisterCommand,
    openDeallocateDialogStableRef,
    openRejectDialogStableRef,
    disabled,
  ]);

  const actionItems = useMemo<BulkActionItem[]>(() => {
    return [
      {
        title: deallocateOnly
          ? ContentId.DeallocateSales
          : ContentId.RejectSales,
        onClick: onChangeClick,
        disabled: isLoading,
      },
    ];
  }, [deallocateOnly, isLoading, onChangeClick]);

  return {
    dropDown: (
      <LaunchBulkDeallocateCancelSales
        key="deallocateCancelSales"
        onClick={onChangeClick}
        disabled={isLoading}
        deallocateOnly={deallocateOnly}
      />
    ),
    actionItems,
  };
};
